import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL ?? '';
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

if (!url || !anonKey) {
  throw new Error(
    '[supabase] VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required. Set them in your CF Pages env and redeploy.',
  );
}

// Per-tab promise-queue lock. Serializes GoTrue critical sections within
// this tab without using the Web Locks API (which caused "Lock was not
// released within 5000ms" stalls on rapid navigation). Cross-tab
// coordination is not needed here.
const tabLocks = new Map<string, Promise<unknown>>();
async function inTabLock<R>(name: string, fn: () => Promise<R>): Promise<R> {
  const prev = tabLocks.get(name) ?? Promise.resolve();
  let release: () => void = () => {};
  const next = new Promise<void>((r) => { release = r; });
  tabLocks.set(name, prev.then(() => next));
  try {
    await prev;
    return await fn();
  } finally {
    release();
    if (tabLocks.get(name) === next) tabLocks.delete(name);
  }
}

export const supabase: SupabaseClient = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    lock: (name, _acquireTimeout, fn) => inTabLock(name, fn),
  },
});
